import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnDestroy, Output, ViewEncapsulation } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';

import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FormManagerService } from '@sim/form';
import { UIListFilters } from '@ui/ui.module';

import { UserModuleConfiguration } from '@sim/users/models/configuration';
import { UsersFilter, UsersFiltersMeta } from '@sim/users/models/filter';
import { Role } from '@sim/users/models/role';
import { UserConfigurationService } from '@sim/users/token';

interface State {
  roles: Role[];
  form?: UntypedFormGroup;
  controls?: { [key: string]: AbstractControl };
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  selector: 'app-filters',
  styleUrls: ['./filters.component.scss'],
  templateUrl: './filters.component.html',
})
export class FiltersComponent implements OnDestroy, UIListFilters<UsersFilter> {

  @Output() readonly apply = new EventEmitter<UsersFilter>();

  readonly state$ = new BehaviorSubject<State>({
    controls: undefined,
    form: undefined,
    roles: [],
  });

  get state() {
    return this.state$.value;
  }

  get data(): UsersFilter | undefined {
    return this.state.form && this.state.form.value;
  }

  private readonly unsubscription$ = new Subject();

  constructor(
    @Inject(UserConfigurationService) private readonly config: UserModuleConfiguration,
    private readonly formManager: FormManagerService,
  ) {
    if (typeof this.config.roles === 'function') {
      const roles$ = this.config.roles();

      if (roles$ instanceof Observable) {
        roles$.subscribe((roles) => this.setState({ roles }));
      }
    }

    const form = this.formManager.makeForm(UsersFiltersMeta);
    const controls = form.controls;

    this.setState({ form, controls });

    form.valueChanges
      .pipe(takeUntil(this.unsubscription$))
      .subscribe((v) => this.apply.emit(v));
  }

  ngOnDestroy() {
    this.unsubscription$.next(null);
    this.unsubscription$.complete();
  }

  private setState(state: Partial<State>) {
    this.state$.next({ ...this.state, ...state });
  }
}
